import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { Fragment, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { processDroppedFiles, type StagedSupplier } from '../../../utils/fileUploadUtils';

interface Props {
  onUploadAll: (suppliers: StagedSupplier[]) => Promise<void>;
  isUploading: boolean;
}

const BulkSubmissionStaging: React.FC<Props> = ({ onUploadAll, isUploading }) => {
  const [stagedSuppliers, setStagedSuppliers] = useState<StagedSupplier[]>([]);
  const [isParsing, setIsParsing] = useState(false);

  const onDrop = async (acceptedFiles: File[]) => {
    setIsParsing(true);
    try {
      const newSuppliers = await processDroppedFiles(acceptedFiles);

      // Merge with existing staged suppliers or add new ones
      setStagedSuppliers((prev) => {
        const merged = [...prev];
        for (const newSup of newSuppliers) {
          const existing = merged.find((s) => s.supplierName === newSup.supplierName);
          if (existing) {
            existing.files.push(...newSup.files);
          } else {
            merged.push(newSup);
          }
        }
        return merged;
      });
    } catch (_e) {
      // Ignore errors parsing dropped files, as they will be marked 'corrupt' or handled per file
    } finally {
      setIsParsing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleNameChange = (supplierId: string, newName: string) => {
    setStagedSuppliers((prev) =>
      prev.map((s) => (s.id === supplierId ? { ...s, supplierName: newName } : s))
    );
  };

  const handleRemoveSupplier = (supplierId: string) => {
    setStagedSuppliers((prev) => prev.filter((s) => s.id !== supplierId));
  };

  const handleRemoveFile = (supplierId: string, fileId: string) => {
    setStagedSuppliers((prev) =>
      prev.map((s) => {
        if (s.id === supplierId) {
          return { ...s, files: s.files.filter((f) => f.id !== fileId) };
        }
        return s;
      })
    );
  };

  const getFileIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckCircleOutlineIcon color="success" />;
      case 'error':
      case 'corrupt':
        return <ErrorOutlineIcon color="error" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const canUpload = stagedSuppliers.some((s) => s.files.some((f) => f.status === 'ok'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 6,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'primary.50' : 'background.paper',
          transition: 'all 0.2s ease',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isParsing ? 'Parsing files...' : 'Drag & drop ZIP files or folders here'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          We will automatically separate files into supplier submissions based on the folder or ZIP
          name.
        </Typography>
      </Box>

      {stagedSuppliers.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Staging Area</Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={!canUpload || isUploading}
              onClick={async () => {
                try {
                  await onUploadAll(stagedSuppliers);
                  setStagedSuppliers([]);
                } catch (_e) {
                  // Errors are generally handled by parent component's snackbar
                }
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload All Valid Submissions'}
            </Button>
          </Box>

          <List
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0,
            }}
          >
            {stagedSuppliers.map((supplier, index) => (
              <Fragment key={supplier.id}>
                {index > 0 && <Divider />}
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: 'column',
                    px: 2,
                    py: 1,
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      size="small"
                      value={supplier.supplierName}
                      onChange={(e) => handleNameChange(supplier.id, e.target.value)}
                      placeholder="Supplier Name"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '1.1rem', fontWeight: 'bold' },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <IconButton
                      onClick={() => handleRemoveSupplier(supplier.id)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <List dense disablePadding sx={{ width: '100%' }}>
                    {supplier.files.map((file) => (
                      <ListItem
                        key={file.id}
                        disableGutters
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveFile(supplier.id, file.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {file.status === 'ok' ? (
                            getFileIcon(file.status)
                          ) : (
                            <Tooltip title={file.errorMessage || 'Invalid file'}>
                              {getFileIcon(file.status)}
                            </Tooltip>
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={file.path !== file.name ? file.path : null}
                          primaryTypographyProps={{
                            color: file.status === 'ok' ? 'text.primary' : 'error',
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </ListItem>
              </Fragment>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default BulkSubmissionStaging;
